import { MoveRight } from "lucide-react";
import {
  PURCHASE_POINTS_CHECK_VALIDITY_NOTICE,
  PURCHASE_POINTS_CONVERT_CANCEL_LABEL,
  PURCHASE_POINTS_CONVERT_CONFIRM_LABEL,
  PURCHASE_POINTS_CONVERT_DIALOG_TITLE,
  PURCHASE_POINTS_CONVERT_DONE_LABEL,
  PURCHASE_POINTS_CONVERT_PENDING_LABEL,
  PURCHASE_POINTS_CONVERT_RECAP,
  PURCHASE_POINTS_CONVERT_SUCCESS_MESSAGE,
  PURCHASE_POINTS_CONVERT_SUCCESS_TITLE,
  PURCHASE_POINTS_CONVERT_TERMS_NOTICE,
  PURCHASE_POINTS_UNIT,
  formatAmountInEuros,
} from "@/features/purchasePoints/config/purchasePoints.config";
import { usePurchasePointsConversion } from "@/features/purchasePoints/hooks/usePurchasePointsConversion";
import { ResponsiveDialog } from "@/shared/components/responsiveDialog";
import { P } from "@/shared/ui/typographies/paragraph";

type ConversionDialogProps = {
  open: boolean
  onClose: () => void
  availablePoints: number
  /** Served by the balance so the rate is never written on this side. */
  pointsPerEuro: number
};

const AMOUNT_BOX = "flex items-center justify-center gap-4 rounded-2xl bg-emerald-50 px-6 py-5 dark:bg-emerald-950/40";
const AMOUNT_TEXT = "text-2xl font-bold text-emerald-700 dark:text-emerald-300";
const ACTIONS_ROW = "flex justify-end gap-3";
const CANCEL_BUTTON = "rounded-full px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700";
const CONFIRM_BUTTON = "rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-50";

/**
 * Confirmation, then the outcome. The confirmation is required because the
 * conversion cannot be undone, and the dialog refuses to be dismissed while
 * the request is in flight.
 */
export const ConversionDialog = ({
  open, onClose, availablePoints, pointsPerEuro,
}: ConversionDialogProps) => {
  const {
    convertPoints, isPending, isSuccess, conversion, reset,
  } = usePurchasePointsConversion();

  const handleClose = () => {
    onClose();
    reset();
  };

  // Any refusal closes the recap: the hook has already said what happened, and
  // a dialog left open over a stale balance would only refuse again.
  const handleConfirm = () => convertPoints(undefined, { onError: handleClose });

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      title={isSuccess ? PURCHASE_POINTS_CONVERT_SUCCESS_TITLE : PURCHASE_POINTS_CONVERT_DIALOG_TITLE}
      dismissible={!isPending}
    >
      {isSuccess && conversion
        ? (
          <div className="flex flex-col gap-5" data-testid="purchase-points-convert-success">
            <div className={AMOUNT_BOX}>
              <span className={AMOUNT_TEXT} data-testid="purchase-points-convert-success-amount">
                {formatAmountInEuros(conversion.amount_cents)}
              </span>
            </div>

            <P size="sm">{PURCHASE_POINTS_CONVERT_SUCCESS_MESSAGE}</P>
            <P size="sm" data-testid="purchase-points-convert-success-expiry">
              {PURCHASE_POINTS_CHECK_VALIDITY_NOTICE}
            </P>

            <div className={ACTIONS_ROW}>
              <button
                type="button"
                onClick={handleClose}
                className={CONFIRM_BUTTON}
                data-testid="purchase-points-convert-done"
              >
                {PURCHASE_POINTS_CONVERT_DONE_LABEL}
              </button>
            </div>
          </div>
        )
        : (
          <div className="flex flex-col gap-5" data-testid="purchase-points-convert-recap">
            <div className={AMOUNT_BOX}>
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                {`${availablePoints.toLocaleString("fr-FR")} ${PURCHASE_POINTS_UNIT}`}
              </span>
              <MoveRight className="w-6 h-6" />
              <span className={AMOUNT_TEXT} data-testid="purchase-points-convert-recap-amount">
                {/* The rate comes from the balance, never from this side. */}
                {formatAmountInEuros(Math.round((availablePoints * 100) / pointsPerEuro))}
              </span>
            </div>

            <P size="sm">{PURCHASE_POINTS_CONVERT_RECAP}</P>
            <P size="sm">{PURCHASE_POINTS_CONVERT_TERMS_NOTICE}</P>

            <div className={ACTIONS_ROW}>
              <button
                type="button"
                onClick={handleClose}
                disabled={isPending}
                className={CANCEL_BUTTON}
                data-testid="purchase-points-convert-cancel"
              >
                {PURCHASE_POINTS_CONVERT_CANCEL_LABEL}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isPending}
                className={CONFIRM_BUTTON}
                data-testid="purchase-points-convert-confirm"
              >
                {isPending ? PURCHASE_POINTS_CONVERT_PENDING_LABEL : PURCHASE_POINTS_CONVERT_CONFIRM_LABEL}
              </button>
            </div>
          </div>
        )}
    </ResponsiveDialog>
  );
};

ConversionDialog.displayName = "ConversionDialog";
